import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const INTERESTS = [
  "AI Employees",
  "Cleaning Robots",
  "Service Robots",
  "Warehouse Robots",
  "Church Solutions",
  "Not Sure",
];
const INDUSTRIES = ["Restaurant", "Hotel", "Healthcare", "Warehouse", "Retail", "Church", "Commercial Cleaning", "Other"];

const schema = z.object({
  name: z.string().trim().min(1).max(120),
  business_name: z.string().trim().max(200).optional(),
  phone: z.string().trim().max(40).optional(),
  email: z.string().trim().email().max(255),
  industry: z.string().optional(),
  message: z.string().trim().max(2000).optional(),
});

const BookDemo = () => {
  const [form, setForm] = useState({
    name: "", business_name: "", phone: "", email: "", industry: "", message: "",
  });
  const [interests, setInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (v: string) =>
    setInterests((i) => (i.includes(v) ? i.filter((x) => x !== v) : [...i, v]));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message || "Please check the form");
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("demo_requests").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      business_name: parsed.data.business_name,
      phone: parsed.data.phone,
      industry: parsed.data.industry,
      message: parsed.data.message,
      interested_in: interests,
    });
    setLoading(false);
    if (error) {
      toast.error("Could not submit. Please call (336) 203-3184.");
      return;
    }
    setDone(true);
    toast.success("Demo request received — we'll be in touch shortly.");
  };

  return (
    <>
      <SEO title="Book a Free Demo | Neil AI Global Solutions" description="Schedule a free demo of Neil AI's AI Employees, PUDU Robotics, and automation systems. See exactly how we help businesses scale." canonical="https://www.neilai.ai/book-demo" />
      <div className="min-h-screen bg-[#05070A] text-white pb-24">
        <Navigation />
        <section className="pt-32 md:pt-40 pb-16">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-10">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Book a Free Demo</h1>
              <p className="text-slate-300">See AI Employees, voice agents, PUDU robotics, and cleaning automation in action — tailored to your business.</p>
            </div>

            <Card className="bg-[#0A1828]/60 border-primary/20 p-6 md:p-8">
              {done ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-[#00F5A0] mx-auto mb-4" />
                  <h2 className="text-2xl font-bold mb-2">Request received</h2>
                  <p className="text-slate-300 mb-6">A Neil AI specialist will reach out within one business day.</p>
                  <Button asChild variant="outline"><Link to="/">Back to home</Link></Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Your name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                    <div><Label>Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                    <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                  </div>
                  <div>
                    <Label>Industry</Label>
                    <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                      <SelectTrigger className="bg-[#05070A] border-primary/30"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-3 block">I'm interested in</Label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {INTERESTS.map((i) => (
                        <label key={i} className="flex items-center gap-2 p-3 rounded-lg bg-[#05070A] border border-primary/20 cursor-pointer hover:border-primary/50">
                          <Checkbox checked={interests.includes(i)} onCheckedChange={() => toggle(i)} />
                          <span className="text-sm">{i}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your operation, challenges, or what you'd like to see." className="bg-[#05070A] border-primary/30" />
                  </div>
                  <Button type="submit" disabled={loading} size="lg" className="w-full bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-bold h-12">
                    {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
                    {loading ? "Submitting…" : "Request Demo"}
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};
export default BookDemo;
